import React from "react";
import { ArrowRightLeft, CheckCircle2, TriangleAlert } from "lucide-react";
import StatusBadge from "../ui/StatusBadge";
import { toMoney } from "./utils";
import { formatPricingDate } from "../../utils/pricingUi";

export default function CalculatieTotalsPanel({
  totals,
  selectedCalculatie,
  applying,
  onApplyToProject,
  canApply,
  tuPunchoutLoading,
  showFinancials = true,
}) {
  const applied = Boolean(selectedCalculatie?.appliedToProjectAt);

  return (
    <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
      <section className="border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-900">Financieel overzicht</h3>
          <p className="mt-1 text-sm text-slate-600">Compact zicht op kost, verkoop en marge tijdens calculeren.</p>
        </div>

        <div className="space-y-3 px-4 py-4">
          <ValueRow label="Arbeid verkoop" value={`€ ${toMoney(totals.laborSell)}`} />
          {showFinancials ? <ValueRow label="Arbeid kost" value={`€ ${toMoney(totals.laborCost)}`} /> : null}
          <ValueRow label="Materiaal verkoop" value={`€ ${toMoney(totals.materialSell)}`} />
          {showFinancials ? <ValueRow label="Materiaal kost" value={`€ ${toMoney(totals.materialCost)}`} /> : null}
          <ValueRow label="Overige posten" value="Voorbereid" muted />
          <ValueRow label="Prijspeildatum" value={formatPricingDate(selectedCalculatie?.pricingDate)} />
          <div className="border-t border-slate-200 pt-3">
            <ValueRow label="Totaal verkoop" value={`€ ${toMoney(totals.totalSell)}`} strong />
            {showFinancials ? <ValueRow label="Totaal kost" value={`€ ${toMoney(totals.totalCost)}`} strong /> : null}
            {showFinancials ? <ValueRow label="Marge" value={`€ ${toMoney(totals.margin)}`} strong tone={totals.margin < 0 ? "danger" : "success"} /> : null}
            {showFinancials ? (
              <ValueRow
                label="Marge %"
                value={`${Number(totals.marginPct || 0).toFixed(1)}%`}
                strong
                tone={totals.marginPct < 0 ? "danger" : "success"}
              />
            ) : null}
          </div>
        </div>
      </section>

      <section className="border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-900">Projectbudget-impact</h3>
          <p className="mt-1 text-sm text-slate-600">Impactactie staat bewust los van gewone documentacties.</p>
        </div>

        <div className="space-y-3 px-4 py-4">
          <div className="flex items-start justify-between gap-3 rounded-sm border border-slate-200 bg-slate-50 px-3 py-3">
            <div>
              <p className="text-sm font-medium text-slate-900">Budgetsync</p>
              <p className="mt-1 text-xs text-slate-600">
                Arbeidsuren, materiaalomzet en totaal verkoop worden overgezet naar het projectbudget.
              </p>
            </div>
            <StatusBadge tone={applied ? "success" : "warning"} label={applied ? "Toegepast" : "Nog niet toegepast"} />
          </div>

          <button
            type="button"
            onClick={onApplyToProject}
            disabled={!canApply || applying}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ArrowRightLeft size={16} />
            {applying ? "Projectbudget bijwerken..." : "Doorzetten naar projectbudget"}
          </button>

          {!canApply ? (
            <div className="flex items-start gap-2 rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              <TriangleAlert size={14} className="mt-0.5 shrink-0" />
              <p>Sla de calculatie eerst op en koppel optioneel een project voordat je projectbudget kunt bijwerken.</p>
            </div>
          ) : null}
        </div>
      </section>

      <section className="border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-900">Prijs- en OCI-status</h3>
          <p className="mt-1 text-sm text-slate-600">Voorbereid op latere prijsstempels en leveranciersvalidatie.</p>
        </div>
        <div className="space-y-3 px-4 py-4 text-sm text-slate-700">
          <div className="flex items-start gap-2 rounded-sm border border-slate-200 bg-slate-50 p-3">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-slate-400" />
            <div>
              <p className="font-medium text-slate-900">OCI import beschikbaar</p>
              <p className="mt-1 text-xs text-slate-600">
                {tuPunchoutLoading ? "Catalogus wordt geopend..." : "Prijzen kunnen via OCI worden opgehaald zonder de calculatieflow te verlaten."}
              </p>
            </div>
          </div>
          <div className="rounded-sm border border-dashed border-slate-300 bg-white p-3 text-xs text-slate-500">
            Per regel is nu een prijsbronbadge beschikbaar. Gebruik prijsactualisatie-preview om snapshots met een nieuwe prijspeildatum te vergelijken.
          </div>
        </div>
      </section>
    </aside>
  );
}

function ValueRow({ label, value, strong = false, tone = "default", muted = false }) {
  const valueClassName = muted
    ? "text-slate-500"
    : tone === "danger"
      ? "text-rose-700"
      : tone === "success"
        ? "text-emerald-700"
        : "text-slate-900";

  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`${strong ? "font-semibold" : "font-medium"} ${valueClassName}`}>{value}</span>
    </div>
  );
}
